import { isObject, some } from 'lodash-es';
import { DateTime, type DurationUnit, type WeekdayNumbers } from 'luxon';
import { type GqlBoardSettings, GqlDayOfWeek, GqlMeetingBase } from '../../graphql/base-types.graphql';

export const weekday: Record<GqlDayOfWeek, WeekdayNumbers> = {
  [GqlDayOfWeek.Monday]: 1,
  [GqlDayOfWeek.Tuesday]: 2,
  [GqlDayOfWeek.Wednesday]: 3,
  [GqlDayOfWeek.Thursday]: 4,
  [GqlDayOfWeek.Friday]: 5,
  [GqlDayOfWeek.Saturday]: 6,
  [GqlDayOfWeek.Sunday]: 7,
};

const defaultConf: GqlBoardSettings = {
  frequency: 8,
  base: GqlMeetingBase.Week,
  dayOfWeek: GqlDayOfWeek.Monday,
};

export function getTomorrowNoon(): DateTime {
  return DateTime.fromObject({ hour: 12 }).plus({ day: 1 });
}

export function getNextWeek(config: GqlBoardSettings) {
  return DateTime.fromObject({ hour: 12 }).plus({ week: 1 }).set({ weekday: weekday[config.dayOfWeek] });
}

export function baseToLuxonBase(base: GqlMeetingBase): {
  base: DurationUnit;
  multiplier: number;
} {
  if (base === GqlMeetingBase.Halfyear) {
    return { base: 'month', multiplier: 6 };
  }
  if (base === GqlMeetingBase.Tertial) {
    return { base: 'month', multiplier: 4 };
  }
  const controlledBase = base as DurationUnit;
  return { base: controlledBase, multiplier: 1 };
}

export function getNextMeetingTime(lastMeetingTime: DateTime, now: DateTime, config: GqlBoardSettings): DateTime {
  if (!isObject(config)) {
    config = defaultConf;
  }
  if (config.frequency < 1 || config.frequency > 31) {
    return getTomorrowNoon();
  }
  const isValidBase = some(GqlMeetingBase, value => {
    return value === config.base;
  });
  if (isValidBase === false) {
    return getTomorrowNoon();
  }
  if (
    weekday[config.dayOfWeek] < weekday[GqlDayOfWeek.Monday] ||
    weekday[config.dayOfWeek] > weekday[GqlDayOfWeek.Sunday]
  ) {
    return getTomorrowNoon();
  }
  const { base, multiplier } = baseToLuxonBase(config.base);
  const calculatedNextMeeting = lastMeetingTime
    .startOf('minute')
    .plus({ [base]: config.frequency * multiplier })
    .set({ weekday: weekday[config.dayOfWeek] });
  if (calculatedNextMeeting < now) {
    return getNextWeek(config);
  }
  return calculatedNextMeeting;
}
